/**
 * 广告管理的**时间范围快捷选项**（用户 2026-10-04 原话第 2 条）。
 *
 * 用户要求（照抄）：快捷选项「类似 shopify 里边的时间范围选项，很丰富。默认是最近7天。
 * 还有：今天、昨天、7天、30天、90天、180天、本月、9月、8月、7月、6月、5月、4月
 * （除本月外的最近6个月）以及自定义时间范围」。
 *
 * ## 为什么是纯函数
 *
 * 「昨天」「本月」「上月」「最近 6 个自然月」在**跨月/跨年/闰年**时最容易算错，
 * 而算错不会报错——只会让人看着 9 月的数据以为是 10 月。因此这里的每一个区间
 * 都在 `dateRange.spec.ts` 里用固定 `now` 逐条钉死。
 *
 * ## 日期口径
 *
 * 业务日 = `YYYY-MM-DD`，用**本地日历日**（与店铺业务日一致；见 R18）。
 * 一律用 `new Date(y, m, d)` 做加减（不要用 `toISOString()`——它按 UTC 切日，
 * 东八区凌晨会整体偏一天）。
 *
 * ## 「最近 N 天」= **不含今天**（2026-10-05 第十五批 · 用户原话第 2/3 条）
 *
 * 用户原话：「最近 7 天，具体是指哪些天？**应该是 -1 ~ -8 天，不含今天**，
 * 最近 XX 天都是类似这个规则」；概览的「最近 30 天…**不应该包括今天**，
 * 也就是 **-1 ~ -31**，否则今天可能刚开始」。
 *
 * 因此 `last_days: N` = **`[今天-N, 今天-1]`**（`N` 天，紧邻的、完整结束的 N 天）。
 * 为什么不能含今天：今天的数据还在增长（广告花费持续累计、订单持续产生），
 * 含今天会让同一个「最近 7 天」链接在一天之内给出不同的数字，且最后一天必然偏低——
 * 看起来像"业绩突然掉了"。
 *
 * 「今天」仍然可**主动选**（`today` 预设），只是不再是 `last_days` 的一部分。
 *
 * ## 「参考右端」`referenceEnd`（2026-10-05 第十七批 · 数据就绪度闸门）
 *
 * 用户确认的口径：「跨平台视图默认 D-2 … 至少是 **N 个整天 对应 N 个整天**」。
 * 为了让"最近 N 天"始终**落在已结算的日子上**，`resolveDateRange` 接受一个
 * **参考右端** `referenceEnd`：
 *
 * ```
 * last_days: N  →  [referenceEnd - (N-1), referenceEnd]      （共 N 天，含右端）
 * ```
 *
 *  · `referenceEnd` 缺省 = **昨天**（真实今天 - 1）→ 逐字等于既有的「不含今天」
 *    `[今天-N, 今天-1]`。**这不是两套规则**：同一公式、只换参考右端；
 *  · 调用方（面向跨平台 / 纯 Meta 视图的页面）把 `referenceEnd` 传成**就绪度右端**
 *    （`readyThroughDate`，见契约 `data-readiness.ts`）→ 默认区间 = 「到数据齐到
 *    那天为止的 N 个整天」，例如概览 `[readyThrough-29, readyThrough]`；
 *  · `today` / `yesterday` / `this_month` 等预设**不受** `referenceEnd` 影响：
 *    用户主动选"今天/昨天"就是要看真实今天/昨天（此时页面会标注"仍在结算"）。
 *
 * ## 自然月选项的口径
 *
 *  · `this_month` = **`[本月 1 号, 昨天]`**（同样不含今天）。⚠️ 边界：**今天就是 1 号**时
 *    该区间为空（`1 号 > 昨天`）→ 回退到**上月整月**（完整、非空、同为月粒度；月初第 1 天
 *    看月报本来就是看上月）。理由见 `resolveDateRange` 的注释。
 *  · 「除本月外的最近 6 个自然月」= **完整自然月**（1 号到月末），不含本月；
 *    列表按由近及远排列，**最近的那一个月显示为「上月」**（它的语义就是上月），
 *    其余显示为「N月」，避免出现两个名字不同、区间相同的按钮。
 *  · 月粒度区间**保持含月末**（不做"不含今天"改造）：它是自然月口径，
 *    与「最近 N 天」是两套不同的东西，混改会让月报对不上。
 */

/** 一个时间范围快捷选项（`custom` 表示用户自己填 from/to）。 */
export type DateRangePreset =
  | { readonly kind: "today" }
  | { readonly kind: "yesterday" }
  | { readonly kind: "last_days"; readonly days: number }
  | { readonly kind: "this_month" }
  | { readonly kind: "last_month" }
  | { readonly kind: "month"; readonly month: string }
  | { readonly kind: "custom" };

export interface IsoRange {
  readonly from: string;
  readonly to: string;
}

/**
 * 「最近 N 天」的**参考右端**（业务日 `YYYY-MM-DD`）。
 *
 * `null` / `undefined` / 非法日期 = 回落到「不含今天」的 `[今天-N, 今天-1]`。
 * 传入就绪度右端（`readyThroughDate`）即得「到数据齐到那天为止的 N 个整天」。
 */
export type RangeReferenceEnd = string | null | undefined;

/**
 * 默认 = 最近 7 天（用户明说「默认是最近7天」）。
 *
 * 区间口径见文件头：**`[今天-7, 今天-1]`（不含今天）**。
 */
export const DEFAULT_DATE_RANGE_KEY = "last7";

/** 「最近 N 天」允许的档位（与用户列出的 7/30/90/180 一致）。 */
export const RECENT_DAY_PRESETS: readonly number[] = [7, 30, 90, 180];

/** 自然月选项个数（除本月外的最近 6 个月）。 */
export const RECENT_MONTH_COUNT = 6;

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_MONTH = /^(\d{4})-(\d{2})$/;

/** 合法业务日：形状正确**且真实存在**（拒绝 `2026-02-30`）。 */
export function isValidIsoDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/** 合法自然月：`YYYY-MM`，月份 01..12。 */
export function isValidIsoMonth(value: string): boolean {
  if (!ISO_MONTH.test(value)) return false;
  const month = Number(value.slice(5, 7));
  return month >= 1 && month <= 12;
}

function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function startOfDay(now: Date): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/**
 * `YYYY-MM-DD` → **本地日历** `Date`（当天 00:00）；非法 / `null` → `null`。
 *
 * 用 `new Date(y, m, d)` 而不是 `new Date(str)`：后者按 UTC 解析，东八区会整体偏一天。
 * 只接受 `isValidIsoDate` 认可的**真实存在**的日期（`2026-02-30` → `null`）。
 */
export function parseLocalIsoDate(value: RangeReferenceEnd): Date | null {
  if (value === null || value === undefined || !isValidIsoDate(value)) return null;
  return new Date(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10)));
}

/** 本地日历日加减（含跨月/跨年/闰年）。 */
export function shiftLocalDays(date: Date, deltaDays: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + deltaDays);
}

export function monthKeyOf(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

/** 某个自然月的闭区间（1 号 → 月末，自动处理闰年与大小月）。 */
export function monthRange(month: string): IsoRange {
  const year = Number(month.slice(0, 4));
  const monthIndex = Number(month.slice(5, 7)) - 1;
  return {
    from: toIsoDate(new Date(year, monthIndex, 1)),
    // 下个月 0 号 = 本月最后一天（JS Date 的合法溢出用法，比 `28/29/30/31` 手工表可靠）。
    to: toIsoDate(new Date(year, monthIndex + 1, 0)),
  };
}

/** 除本月外的最近 `count` 个自然月（由近及远，`YYYY-MM`）。 */
export function recentMonthKeys(now: Date, count: number = RECENT_MONTH_COUNT): readonly string[] {
  const keys: string[] = [];
  for (let offset = 1; offset <= count; offset += 1) {
    keys.push(monthKeyOf(new Date(now.getFullYear(), now.getMonth() - offset, 1)));
  }
  return keys;
}

/** preset → URL 里的稳定 key（默认值 `last7`）。 */
export function dateRangeKey(preset: DateRangePreset): string {
  switch (preset.kind) {
    case "today":
      return "today";
    case "yesterday":
      return "yesterday";
    case "last_days":
      return `last${preset.days}`;
    case "this_month":
      return "this_month";
    case "last_month":
      return "last_month";
    case "month":
      return `month:${preset.month}`;
    case "custom":
      return "custom";
  }
}

/** URL key → preset；未知/垃圾值 → 默认「最近 7 天」（URL 可手改，解析必须免疫）。 */
export function parseDateRangeKey(raw: string | null | undefined): DateRangePreset {
  const value = (raw ?? "").trim();
  switch (value) {
    case "today":
      return { kind: "today" };
    case "yesterday":
      return { kind: "yesterday" };
    case "this_month":
      return { kind: "this_month" };
    case "last_month":
      return { kind: "last_month" };
    case "custom":
      return { kind: "custom" };
    default:
      break;
  }
  const recent = /^last(\d+)$/.exec(value);
  if (recent !== null) {
    const days = Number(recent[1]);
    if (RECENT_DAY_PRESETS.includes(days)) return { kind: "last_days", days };
  }
  if (value.startsWith("month:")) {
    const month = value.slice("month:".length);
    if (isValidIsoMonth(month)) return { kind: "month", month };
  }
  return { kind: "last_days", days: 7 };
}

/**
 * preset → 闭区间。`custom` 返回 `null`（区间由调用方自己的 from/to 决定，
 * 本函数**不猜**用户填了什么）。
 *
 * ⚠️ 2026-10-05 起：
 *  · `last_days` = **`[referenceEnd-(N-1), referenceEnd]`**（共 N 天，含右端）；
 *    `referenceEnd` 缺省 = 昨天 → 即既有的「不含今天」`[今天-N, 今天-1]`，
 *    传就绪度右端 → 「到数据齐到那天为止的 N 个整天」（见文件头）；
 *  · `this_month` = `[1 号, 昨天]`（不含今天）；
 *  · `today` / `yesterday` / `last_month` / `month` 语义不变，**不受** `referenceEnd` 影响。
 */
export function resolveDateRange(preset: DateRangePreset, now: Date, referenceEnd?: RangeReferenceEnd): IsoRange | null {
  const today = startOfDay(now);
  switch (preset.kind) {
    case "today":
      return { from: toIsoDate(today), to: toIsoDate(today) };
    case "yesterday": {
      const yesterday = shiftLocalDays(today, -1);
      return { from: toIsoDate(yesterday), to: toIsoDate(yesterday) };
    }
    case "last_days": {
      const days = preset.days >= 1 ? Math.trunc(preset.days) : 1;
      // 参考右端：显式合法日期优先，否则回落到「昨天」（= 不含今天）。
      const end = parseLocalIsoDate(referenceEnd) ?? shiftLocalDays(today, -1);
      // 共 N 天、含右端：左端 = 右端 - (N-1)。`end` = 昨天时与 `[今天-N, 今天-1]` 逐字相同。
      return { from: toIsoDate(shiftLocalDays(end, -(days - 1))), to: toIsoDate(end) };
    }
    case "this_month": {
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const yesterday = shiftLocalDays(today, -1);
      // ⚠️ 边界（用户第 3 条要求给出明确行为）：今天就是 1 号时「本月至今（不含今天）」
      // 是**空区间**（1 号 > 昨天）——空区间会被后端判成非法区间（from > to），
      // 或者给出一个全空的屏，用户只会以为"数据坏了"。
      //
      // 选择：回退到**上月整月**。理由：
      //  1. 非空且是**完整**的自然月（可读、可对账），不是"一天"这种挂错标签的怪区间；
      //  2. 与「本月」的**月粒度**语义最接近（月初第 1 天看月报，本来就看上月）；
      //  3. 绝不回退到"昨天"——那会让「本月」按钮只显示 1 天，比空区间更误导。
      // 页面必须**显式显示解析出来的区间**（概览/广告管理页头都有区间 chip），
      // 因此用户一眼就能看到"本月"此刻落在上月整月，不会被标签骗。
      if (yesterday < firstOfMonth) {
        return monthRange(monthKeyOf(new Date(today.getFullYear(), today.getMonth() - 1, 1)));
      }
      return { from: toIsoDate(firstOfMonth), to: toIsoDate(yesterday) };
    }
    case "last_month":
      return monthRange(monthKeyOf(new Date(today.getFullYear(), today.getMonth() - 1, 1)));
    case "month":
      return monthRange(preset.month);
    case "custom":
      return null;
  }
}

/** 今天的业务日（`YYYY-MM-DD`，本地日历日）。 */
export function todayIso(now: Date = new Date()): string {
  return toIsoDate(startOfDay(now));
}

/**
 * 反推：`[from, to]` 恰好等于哪个**快捷选项**的区间？→ 该选项的 URL key；都不是 → `null`。
 *
 * 为什么需要：URL 里 `range` 可能缺省或不可识别（例如共享的序列化规则会省略
 * 全站默认档 `last7`），但**按钮高亮必须还原**——否则用户选了「最近 7 天」，
 * 刷新后按钮跳到「自定义」，看起来像区间被改了。
 *
 * 用 `dateRangeOptions`（渲染列表，唯一顺序源）逐条比对，**第一个命中即返回**：
 * 顺序 = 展示顺序，因此「本月」在上月整月之前命中（1 号的回退区间同时等于
 * `month:上月`，此时按「本月」按钮高亮才是对的——用户点的就是本月）。
 *
 * ⚠️ `referenceEnd` 必须与解析该区间时用的一致（就绪度右端 / 缺省=昨天），
 * 否则「最近 N 天」的比对会整体差一天，按钮会莫名跳到「自定义」。
 */
export function inferDateRangeKey(
  from: string,
  to: string,
  now: Date = new Date(),
  referenceEnd?: RangeReferenceEnd,
): string | null {
  if (!isValidIsoDate(from) || !isValidIsoDate(to) || from > to) return null;
  for (const option of dateRangeOptions(now)) {
    const resolved = resolveDateRange(option.preset, now, referenceEnd);
    if (resolved !== null && resolved.from === from && resolved.to === to) return option.key;
  }
  return null;
}

export interface DateRangeOption {
  readonly key: string;
  readonly label: string;
  readonly preset: DateRangePreset;
}

/** 月份按钮的中文标签（`2026-09` → `9月`；去掉前导零）。 */
export function monthLabel(month: string): string {
  return `${Number(month.slice(5, 7))}月`;
}

/**
 * 渲染用的快捷选项列表（顺序即用户列出的顺序）。
 *
 * 本月 → 上月（= 最近 6 个自然月里的第一个）→ 再往前 5 个月 → 自定义。
 * **不出现两个区间相同的按钮**：最近的自然月只以「上月」出现一次。
 */
export function dateRangeOptions(now: Date): readonly DateRangeOption[] {
  const options: DateRangeOption[] = [
    { key: "today", label: "今天", preset: { kind: "today" } },
    { key: "yesterday", label: "昨天", preset: { kind: "yesterday" } },
    { key: "last7", label: "最近 7 天", preset: { kind: "last_days", days: 7 } },
    { key: "last30", label: "最近 30 天", preset: { kind: "last_days", days: 30 } },
    { key: "last90", label: "最近 90 天", preset: { kind: "last_days", days: 90 } },
    { key: "last180", label: "最近 180 天", preset: { kind: "last_days", days: 180 } },
    { key: "this_month", label: "本月", preset: { kind: "this_month" } },
  ];
  recentMonthKeys(now).forEach((month, index) => {
    options.push({
      key: `month:${month}`,
      label: index === 0 ? `上月（${monthLabel(month)}）` : monthLabel(month),
      preset: { kind: "month", month },
    });
  });
  options.push({ key: "custom", label: "自定义", preset: { kind: "custom" } });
  return options;
}
